import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, UtensilsCrossed, Sparkles, Heart, Star, Leaf, Clock, Users, Truck, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Brand logo URL (transparent background)
const BRAND_LOGO = "https://customer-assets.emergentagent.com/job_17ae6403-2082-451f-978f-8e823488d32d/artifacts/bd5nly0b_tastes%20good%20does%20good%20%20group%20logo%20%20%287%29.png";

// Experience previews
const EXPERIENCES = [
  {
    id: 'cream-tea',
    name: 'Cream Tea Masterclass',
    price: 45,
    duration: '3 hours',
    image: 'https://www.cakerywonderland.com/cdn/shop/files/britain-loves-baking-academy-3-hour-cream-tea-masterclassbaking-masterclasswonderland-experiences-1726264.png?v=1754436424&width=600'
  },
  {
    id: 'bridgerton',
    name: 'Bridgerton Cake Decorating',
    price: 65,
    duration: '2.5 hours',
    image: 'https://www.cakerywonderland.com/cdn/shop/files/regency-elegance-bridgerton-inspired-cake-decorating-masterclass-and-cocktailswonderland-experienceswonderland-experiences-294578.png?v=1744126383&width=600'
  },
  {
    id: 'dessert-tower',
    name: 'Dessert Tower Experience',
    price: 90,
    duration: '3 hours',
    image: 'https://www.cakerywonderland.com/cdn/shop/files/unleash-your-inner-pastry-chef-dessert-tower-making-cocktail-experiencedessert-experiencecakery-wonderland-events-991485.jpg?v=1739058779&width=600'
  }
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-background pt-16" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-gradient min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent"></div>
        <div className="absolute top-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-8"
            >
              <img 
                src={BRAND_LOGO} 
                alt="Cakery Wonderland" 
                className="h-32 sm:h-40 w-auto mx-auto animate-float drop-shadow-[0_0_30px_rgba(255,0,144,0.4)]"
                data-testid="hero-logo"
              />
            </motion.div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-8 leading-tight" data-testid="hero-title">
              The UK's First
              <span className="block bg-gradient-to-r from-primary via-pink-500 to-primary bg-clip-text text-transparent animate-shimmer" style={{backgroundSize: '200% auto'}}>
                Purpose Driven Cakery
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto mb-6 font-body leading-relaxed" data-testid="hero-description">
              Specialty Cakery & Afternoon Tea Caterers
            </p>
            <p className="text-base text-primary font-semibold mb-10">
              Creating food experiences that taste good and do good.
            </p>
            
            {/* 3 Main CTAs */}
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/catering">
                <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-primary/50 hover:shadow-primary/70 button-primary" data-testid="book-catering-btn">
                  <UtensilsCrossed className="w-5 h-5 mr-2" />
                  Book Catering
                </Button>
              </Link>
              <Link to="/subscriptions">
                <Button
                  className="bg-white text-background hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold"
                  data-testid="buy-subscription-btn">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Buy a Subscription
                </Button>
              </Link>
              <Link to="/experiences">
                <Button
                  variant="outline"
                  className="border-2 border-white/20 text-white hover:bg-white/10 hover:border-primary rounded-full px-8 py-6 text-lg backdrop-blur-sm"
                  data-testid="book-experience-btn">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Experiences
                </Button>
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="mt-12 flex items-center justify-center gap-6 flex-wrap text-sm text-text-muted">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-primary" />
                <span>15% to Charity</span>
              </div>
              <div className="w-px h-4 bg-white/20 hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-primary fill-primary" />
                <span>5 Star Reviews</span>
              </div>
              <div className="w-px h-4 bg-white/20 hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <Leaf className="w-5 h-5 text-primary" />
                <span>Sustainable</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Subscriptions Section - MOVED UP */}
      <section className="py-20 bg-background-elevated" data-testid="subscriptions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Sparkles className="w-10 h-10 text-primary mx-auto mb-4" />
              <h2 className="text-4xl font-heading font-bold text-white mb-4">Monthly Subscriptions</h2>
              <p className="text-lg text-text-secondary font-body max-w-2xl mx-auto">
                Treat yourself to regular deliveries of our exquisite treats with Track 24 next-day Royal Mail delivery.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Afternoon Tea Subscription */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-background-paper rounded-2xl p-8 border-2 border-primary/20 hover:border-primary/50 transition-all relative"
            >
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary text-white text-center py-2 px-6 rounded-full text-sm font-bold shadow-lg shadow-primary/30">
                First 3 months for £60
              </div>
              <div className="mt-4">
                <h3 className="text-2xl font-heading font-bold text-white mb-2">Afternoon Tea Subscription</h3>
                <p className="text-text-secondary font-body text-sm mb-4">Monthly delivery of our signature afternoon tea collection</p>
                <div className="mb-4 p-4 bg-background-elevated rounded-xl">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-accent text-primary">£60</span>
                    <span className="text-text-muted font-body">for first 3 months</span>
                  </div>
                  <p className="text-text-secondary text-sm">Then £27.50/month after</p>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-text-secondary text-sm"><Check className="w-4 h-4 text-primary mr-2" />Track 24 next-day delivery</li>
                  <li className="flex items-center text-text-secondary text-sm"><Check className="w-4 h-4 text-primary mr-2" />15% goes to charity</li>
                </ul>
                <Link to="/subscriptions">
                  <Button className="w-full bg-primary hover:bg-primary-hover text-white rounded-full">
                    Subscribe Now
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Cakery Subscription */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-background-paper rounded-2xl p-8 border-2 border-primary/20 hover:border-primary/50 transition-all relative"
            >
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary text-white text-center py-2 px-6 rounded-full text-sm font-bold shadow-lg shadow-primary/30">
                First 3 months for £45
              </div>
              <div className="mt-4">
                <h3 className="text-2xl font-heading font-bold text-white mb-2">Cakery Subscription</h3>
                <p className="text-text-secondary font-body text-sm mb-4">Monthly selection of artisan cakes and treats</p>
                <div className="mb-4 p-4 bg-background-elevated rounded-xl">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-accent text-primary">£45</span>
                    <span className="text-text-muted font-body">for first 3 months</span>
                  </div>
                  <p className="text-text-secondary text-sm">Then £20/month after</p>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-text-secondary text-sm"><Check className="w-4 h-4 text-primary mr-2" />Track 24 next-day delivery</li>
                  <li className="flex items-center text-text-secondary text-sm"><Check className="w-4 h-4 text-primary mr-2" />15% goes to charity</li>
                </ul>
                <Link to="/subscriptions">
                  <Button className="w-full bg-primary hover:bg-primary-hover text-white rounded-full">
                    Subscribe Now
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-background" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Our Services</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Catering */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-background-paper rounded-2xl p-8 border border-primary/20 hover:border-primary/50 transition-all group"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <UtensilsCrossed className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-white mb-3">Book Catering</h3>
              <p className="text-text-secondary font-body mb-6">
                From intimate gatherings to grand celebrations. Afternoon tea, dessert tables, weddings & corporate events.
              </p>
              <div className="flex items-center gap-4 text-sm text-text-muted mb-6">
                <span className="flex items-center gap-1">
                  <Star className="w-4 h-4" /> Award winning
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" /> 10+ guests
                </span>
              </div>
              <Link to="/catering">
                <Button className="w-full bg-primary hover:bg-primary-hover text-white rounded-full" data-testid="catering-cta">
                  £200 Deposit - Enquire
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            {/* Experiences */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-background-paper rounded-2xl p-8 border border-primary/20 hover:border-primary/50 transition-all group"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <Calendar className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-white mb-3">Book Experiences</h3>
              <p className="text-text-secondary font-body mb-6">
                Immersive baking masterclasses with cocktails. From Cream Tea to Bridgerton-themed cake decorating.
              </p>
              <div className="flex items-center gap-4 text-sm text-text-muted mb-6">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" /> 2.5-3 hours
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" /> Groups welcome
                </span>
              </div>
              <Link to="/experiences">
                <Button className="w-full bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-full" data-testid="experiences-cta">
                  From £45 - Book Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Experiences Preview */}
      <section className="py-20 bg-background-elevated" data-testid="experiences-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Calendar className="w-10 h-10 text-primary mx-auto mb-4" />
              <h2 className="text-4xl font-heading font-bold text-white mb-4">Our Experiences</h2>
              <p className="text-lg text-text-secondary font-body max-w-2xl mx-auto">
                Immersive masterclasses with award-winning chefs. Create, learn, and indulge.
              </p>
            </motion.div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {EXPERIENCES.map((exp, index) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link to="/experiences">
                  <div className="bg-background-paper rounded-2xl overflow-hidden product-card-hover group h-full">
                    <div className="aspect-[4/3] overflow-hidden relative">
                      <img
                        src={exp.image}
                        alt={exp.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute top-3 right-3">
                        <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-bold">
                          £{exp.price}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-heading font-semibold text-white mb-2">
                        {exp.name}
                      </h3>
                      <div className="flex items-center gap-3 text-text-secondary text-sm">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-primary" />
                          {exp.duration}
                        </span>
                        <span className="text-primary font-medium">Book Now →</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link to="/experiences">
              <Button className="bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-full px-10 py-5 text-lg font-semibold transition-all">
                View All Experiences
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About / Purpose Section */}
      <section className="py-20 bg-background" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-background-paper rounded-3xl p-8 border border-primary/20 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-48 h-48 bg-primary/10 rounded-full blur-3xl"></div>
              <div className="relative z-10">
                <Heart className="w-10 h-10 text-primary mb-4" />
                <h2 className="text-3xl font-heading font-bold text-white mb-4">
                  Our Purpose
                </h2>
                <p className="text-text-secondary font-body mb-4 leading-relaxed">
                  We have an unwavering commitment to making a difference. We dedicate <span className="text-primary font-bold">15% of our revenue</span> to fighting food poverty by supporting food charities and incubating new food businesses.
                </p>
                <p className="text-white font-body italic border-l-2 border-primary pl-4">
                  "Creating memorable food experiences that not only taste good, but do good."
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bg-background-paper rounded-2xl p-6 border border-primary/20">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <Star className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-white mb-1">Award Winning</h3>
                    <p className="text-text-secondary text-sm">UK's most sustainable caterer three years in a row. Over 23,000 traditional teas created in 2024.</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-paper rounded-2xl p-6 border border-primary/20">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <Leaf className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-white mb-1">Sustainability</h3>
                    <p className="text-text-secondary text-sm">Hyper-short supply chains (just 1km!), knowing every supplier personally, and zero food to landfill.</p>
                  </div>
                </div>
              </div>
              <div className="bg-background-paper rounded-2xl p-6 border border-primary/20">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-white mb-1">Expert Team</h3>
                    <p className="text-text-secondary text-sm">Led by cookbook author Greg Wixted and a diverse team with over 40 years of experience creating memorable events.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-10 h-10 text-primary mx-auto mb-6" />
            <h2 className="text-4xl font-heading font-bold text-white mb-4">
              Ready to Experience Wonderland?
            </h2>
            <p className="text-lg text-text-secondary mb-8">
              Book catering, subscribe for monthly deliveries, or book an experience
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/catering">
                <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-5 text-lg font-semibold shadow-lg shadow-primary/50 button-primary">
                  <UtensilsCrossed className="w-5 h-5 mr-2" />
                  Book Catering
                </Button>
              </Link>
              <Link to="/subscriptions">
                <Button className="bg-white text-background hover:bg-white/90 rounded-full px-8 py-5 text-lg font-semibold">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Buy a Subscription
                </Button>
              </Link>
              <Link to="/experiences">
                <Button variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-5 text-lg">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Experiences
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
